import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Eye, EyeOff, Info, Upload, CalendarClock, X, ArrowUp, ArrowDown, Film } from 'lucide-react';
import api, { errText, uploadImage, mediaUrl } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { PRESET_BACKGROUNDS, PRESET_VIDEOS } from '../../lib/heroBackgrounds';

const THEMES = [
  { key: 'floral', label: 'Floral', swatch: 'linear-gradient(135deg,#F7D5DA,#F5C7CD)' },
  { key: 'rose', label: 'Rose glow', swatch: 'linear-gradient(135deg,#F5C7CD,#E79AA6)' },
  { key: 'burgundy', label: 'Burgundy', swatch: 'linear-gradient(135deg,#4A1721,#7A2233)' },
  { key: 'cream', label: 'Cream', swatch: 'linear-gradient(135deg,#FBF3EC,#F3E2D6)' },
  { key: 'dusk', label: 'Dusk', swatch: 'linear-gradient(135deg,#7A2233,#C97C86)' },
];

const blankSlide = () => ({ _k: `new_${Math.random().toString(36).slice(2, 9)}`, title: '', highlight: '', subtitle: '', bg_theme: 'floral', bg_type: 'theme', image: '', video_url: '', start_date: '', end_date: '', primary_label: '', primary_link: '', secondary_label: '', secondary_link: '', active: true });

const SlideBackground = ({ s, onChange }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const bt = s.bg_type || (s.video_url ? 'video' : (s.image ? 'photo' : 'theme'));
  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { const url = await uploadImage(file); onChange('image', url); toast({ title: 'Photo uploaded' }); }
    catch (e) { toast({ title: 'Upload failed', description: errText(e) }); }
    finally { setUploading(false); }
  };
  const PhotoPicker = ({ label }) => (
    <div>
      {label && <span className="text-[11px] uppercase tracking-widest text-mahogany/55">{label}</span>}
      {s.image && (
        <div className="mt-1.5 flex items-center gap-3">
          <div className="w-28 h-16 rounded-xl bg-cover bg-center border border-rose/40" style={{ backgroundImage: `url(${mediaUrl(s.image)})` }} />
          <button type="button" onClick={() => onChange('image', '')} className="pill-btn-outline text-xs py-1" data-testid="bg-clear"><X className="w-3.5 h-3.5" /> Remove</button>
        </div>
      )}
      <div className="mt-1.5 flex flex-wrap gap-2 items-center" data-testid="bg-presets">
        {PRESET_BACKGROUNDS.map((p) => (
          <button key={p.key} type="button" onClick={() => onChange('image', p.url)} title={p.label}
            className={`w-20 h-14 rounded-lg bg-cover bg-center border-2 transition ${s.image === p.url ? 'border-mahogany ring-2 ring-mahogany/40' : 'border-transparent hover:border-rose'}`}
            style={{ backgroundImage: `url(${p.url})` }} data-testid={`bg-preset-${p.key}`} />
        ))}
        <label className="w-20 h-14 rounded-lg border-2 border-dashed border-rose/50 flex flex-col items-center justify-center text-[10px] text-mahogany/60 cursor-pointer hover:bg-rose-soft/30" data-testid="bg-upload">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mb-0.5" /> Upload</>}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => doUpload(e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );
  return (
    <div className="mt-3">
      <span className="text-xs uppercase tracking-widest text-mahogany/70">Background</span>
      <div className="mt-1.5 flex gap-2" data-testid="bg-type">
        {[['theme', 'Color theme'], ['photo', 'Photo'], ['video', 'Video']].map(([v, l]) => (
          <button key={v} type="button" onClick={() => onChange('bg_type', v)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${bt === v ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50'}`}
            data-testid={`bg-type-${v}`}>{l}</button>
        ))}
      </div>

      {bt === 'theme' && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="bg-themes">
          {THEMES.map((th) => (
            <button key={th.key} type="button" onClick={() => onChange('bg_theme', th.key)} title={th.label}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full border text-xs transition-colors ${s.bg_theme === th.key ? 'border-mahogany ring-1 ring-mahogany' : 'border-rose/40'}`}
              data-testid={`bg-theme-${th.key}`}>
              <span className="w-4 h-4 rounded-full border border-white/60" style={{ background: th.swatch }} /> {th.label}
            </button>
          ))}
        </div>
      )}

      {bt === 'photo' && <div className="mt-2"><PhotoPicker /></div>}

      {bt === 'video' && (
        <div className="mt-2 space-y-2">
          <input value={s.video_url || ''} onChange={(e) => onChange('video_url', e.target.value)}
            placeholder="Direct .mp4 URL (from Pexels / Coverr or your own)"
            className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="bg-video" />
          <div className="flex flex-wrap gap-2" data-testid="bg-video-presets">
            {PRESET_VIDEOS.map((v) => (
              <button key={v.key} type="button" onClick={() => onChange('video_url', v.url)}
                className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${s.video_url === v.url ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50'}`}
                data-testid={`bg-video-${v.key}`}>{v.label}</button>
            ))}
            {s.video_url && <button type="button" onClick={() => onChange('video_url', '')} className="px-2.5 py-1 rounded-full border border-rose/50 text-xs text-mahogany/70" data-testid="bg-video-clear">Clear video</button>}
          </div>
          <PhotoPicker label="Poster image (shown while loading & on mobile)" />
          <p className="text-[11px] text-mahogany/50">Video plays muted &amp; looping on desktop; phones show the poster to save data.</p>
        </div>
      )}
    </div>
  );
};

const HeroSection = () => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/home/hero').then((r) => setCfg(r.data)).catch(() => setCfg({ autoplay_ms: 6000, include_events: true, max_events: 3, event_mode: 'auto', event_slugs: [], slides: [] }));
    api.get('/admin/home/events').then((r) => setEvents(r.data || [])).catch(() => setEvents([]));
  }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const setSlide = (i, k, v) => setCfg((p) => ({ ...p, slides: p.slides.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) }));
  const addSlide = () => setCfg((p) => ({ ...p, slides: [...p.slides, blankSlide()] }));
  const rmSlide = (i) => setCfg((p) => ({ ...p, slides: p.slides.filter((_, idx) => idx !== i) }));
  const move = (i, dir) => setCfg((p) => { const a = [...p.slides]; const j = i + dir; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return { ...p, slides: a }; });

  const evSel = (cfg?.event_slugs || []);
  const evBySlug = Object.fromEntries(events.map((e) => [e.slug, e]));
  const addEvent = (slug) => set('event_slugs', [...evSel, slug]);
  const rmEvent = (slug) => set('event_slugs', evSel.filter((s) => s !== slug));
  const moveEvent = (i, dir) => { const a = [...evSel]; const j = i + dir; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; set('event_slugs', a); };

  const save = async () => {
    for (const s of cfg.slides) if (!s.title.trim()) return toast({ title: 'Every slide needs a title' });
    setSaving(true);
    try {
      const r = await api.put('/admin/home/hero', {
        autoplay_ms: Number(cfg.autoplay_ms) || 6000,
        include_events: !!cfg.include_events,
        max_events: Number(cfg.max_events) || 0,
        event_mode: cfg.event_mode || 'auto',
        event_slugs: cfg.event_mode === 'featured' ? evSel : [],
        events_first: !!cfg.events_first,
        slides: cfg.slides.map((s) => ({
          ...(s.id ? { id: s.id } : {}),
          title: s.title.trim(), highlight: (s.highlight || '').trim(), subtitle: (s.subtitle || '').trim(), bg_theme: s.bg_theme, bg_type: s.bg_type || (s.video_url ? 'video' : (s.image ? 'photo' : 'theme')), image: (s.image || '').trim(), video_url: (s.video_url || '').trim(),
          start_date: s.start_date || '', end_date: s.end_date || '',
          primary_label: (s.primary_label || '').trim(), primary_link: (s.primary_link || '').trim(),
          secondary_label: (s.secondary_label || '').trim(), secondary_link: (s.secondary_link || '').trim(),
          active: !!s.active,
        })),
      });
      setCfg(r.data); toast({ title: 'Home hero saved' });
    } catch (e) { toast({ title: 'Could not save', description: errText(e) }); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-6" data-testid="admin-home-hero">
      <div className="rounded-3xl bg-white/70 border border-rose/40 p-5 grid sm:grid-cols-3 gap-4">
        <label className="text-xs uppercase tracking-widest text-mahogany/70">Autoplay (seconds)
          <input type="number" min="2" max="20" value={Math.round((cfg.autoplay_ms || 6000) / 1000)} onChange={(e) => set('autoplay_ms', (Number(e.target.value) || 6) * 1000)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid="hero-autoplay" />
        </label>
        <label className="text-xs uppercase tracking-widest text-mahogany/70 flex flex-col">Auto-add upcoming events
          <span className="mt-2 inline-flex items-center gap-2 text-sm normal-case tracking-normal text-mahogany"><input type="checkbox" checked={!!cfg.include_events} onChange={(e) => set('include_events', e.target.checked)} data-testid="hero-include-events" /> Show events as slides</span>
        </label>
        <label className="text-xs uppercase tracking-widest text-mahogany/70">Max event slides
          <input type="number" min="0" max="6" value={cfg.max_events} onChange={(e) => set('max_events', Number(e.target.value))} disabled={!cfg.include_events} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm disabled:opacity-50" data-testid="hero-max-events" />
        </label>
      </div>

      {cfg.include_events && (
        <div className="rounded-3xl bg-white/70 border border-rose/40 p-5" data-testid="hero-events-config">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-xs uppercase tracking-widest text-mahogany/70">Which events</span>
            {[['auto', 'Auto (soonest upcoming)'], ['featured', 'Hand-picked']].map(([v, l]) => (
              <button key={v} onClick={() => set('event_mode', v)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${(cfg.event_mode || 'auto') === v ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50'}`} data-testid={`hero-event-mode-${v}`}>{l}</button>
            ))}
          </div>
          <label className="mt-4 flex items-start gap-2.5 cursor-pointer" data-testid="hero-events-first-label">
            <input type="checkbox" checked={!!cfg.events_first} onChange={(e) => set('events_first', e.target.checked)} data-testid="hero-events-first" className="mt-0.5 h-4 w-4 accent-mahogany" />
            <span className="text-sm text-mahogany leading-snug">
              Show events first
              <span className="block text-xs text-mahogany/60">Event slides load before your manual slides — the first featured/upcoming event becomes the opening slide.</span>
            </span>
          </label>
          {(cfg.event_mode === 'featured') && (
            <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="hero-event-picker">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1.5">Featured (in order, capped at Max)</div>
                {evSel.length === 0 && <p className="text-xs text-mahogany/50">Add events from the right.</p>}
                <div className="space-y-1.5">
                  {evSel.map((slug, i) => (
                    <div key={slug} className="flex items-center gap-2 rounded-xl bg-cream/50 border border-rose/30 px-2.5 py-1.5" data-testid={`hero-event-chosen-${slug}`}>
                      <span className="text-sm text-burgundy flex-1 truncate">{evBySlug[slug]?.title || slug}</span>
                      <button onClick={() => moveEvent(i, -1)} disabled={i === 0} className="p-1 text-mahogany/60 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                      <button onClick={() => moveEvent(i, 1)} disabled={i === evSel.length - 1} className="p-1 text-mahogany/60 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                      <button onClick={() => rmEvent(slug)} className="p-1 text-red-500/70 hover:text-red-600" data-testid={`hero-event-remove-${slug}`}><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1.5">Available events</div>
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {events.filter((e) => !evSel.includes(e.slug)).map((e) => (
                    <button key={e.slug} onClick={() => addEvent(e.slug)} className="w-full flex items-center gap-2 rounded-xl bg-white border border-rose/30 px-2.5 py-1.5 text-left hover:bg-rose-soft/30" data-testid={`hero-event-add-${e.slug}`}>
                      <Plus className="w-3.5 h-3.5 text-mahogany/60" />
                      <span className="text-sm text-burgundy flex-1 truncate">{e.title}</span>
                    </button>
                  ))}
                  {events.filter((e) => !evSel.includes(e.slug)).length === 0 && <p className="text-xs text-mahogany/50">No more events.</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="font-serif-display text-2xl text-burgundy">Slides</h2>
        <button onClick={addSlide} className="pill-btn" data-testid="hero-add-slide"><Plus className="w-4 h-4" /> Add slide</button>
      </div>
      {cfg.slides.length === 0 && <p className="text-sm text-mahogany/60">No manual slides yet. {cfg.include_events ? 'Upcoming events still show automatically.' : 'The landing page uses the default hero.'}</p>}

      <div className="space-y-4">
        {cfg.slides.map((s, i) => (
          <div key={s.id || s._k || i} className={`rounded-3xl border p-5 ${s.active ? 'bg-white/70 border-rose/40' : 'bg-mahogany/5 border-mahogany/20 opacity-80'}`} data-testid={`hero-slide-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-xs px-2 py-1 rounded border border-rose/40 disabled:opacity-30" data-testid={`hero-up-${i}`}>↑</button>
                <button onClick={() => move(i, 1)} disabled={i === cfg.slides.length - 1} className="text-xs px-2 py-1 rounded border border-rose/40 disabled:opacity-30" data-testid={`hero-down-${i}`}>↓</button>
                <span className="text-xs text-mahogany/50 ml-1">Slide {i + 1}</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setSlide(i, 'active', !s.active)} className={`text-xs inline-flex items-center gap-1 px-2 py-1 rounded-full border ${s.active ? 'text-green-700 border-green-300 bg-green-50' : 'text-mahogany/60 border-mahogany/20'}`} data-testid={`hero-active-${i}`}>{s.active ? <><Eye className="w-3.5 h-3.5" /> Visible</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>}</button>
                <button onClick={() => rmSlide(i)} className="text-red-500/70 hover:text-red-600 p-1.5" data-testid={`hero-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input value={s.title} onChange={(e) => setSlide(i, 'title', e.target.value)} placeholder="Headline (e.g. Join our online classes)" className="bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid={`hero-title-${i}`} />
              <input value={s.highlight || ''} onChange={(e) => setSlide(i, 'highlight', e.target.value)} placeholder="Accent word (italic, e.g. Awareness) — optional" className="bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid={`hero-highlight-${i}`} />
              <input value={s.subtitle || ''} onChange={(e) => setSlide(i, 'subtitle', e.target.value)} placeholder="Message / subtitle" className="sm:col-span-2 bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid={`hero-subtitle-${i}`} />
            </div>

            <SlideBackground s={s} onChange={(k, v) => setSlide(i, k, v)} />

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <label className="text-[11px] uppercase tracking-widest text-mahogany/60 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Show from (optional)
                <input type="date" value={s.start_date || ''} onChange={(e) => setSlide(i, 'start_date', e.target.value)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm normal-case tracking-normal" data-testid={`hero-start-${i}`} />
              </label>
              <label className="text-[11px] uppercase tracking-widest text-mahogany/60 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> Show until (optional)
                <input type="date" value={s.end_date || ''} onChange={(e) => setSlide(i, 'end_date', e.target.value)} className="mt-1 w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm normal-case tracking-normal" data-testid={`hero-end-${i}`} />
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div className="rounded-2xl bg-cream/40 p-3">
                <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1.5">Primary button</div>
                <input value={s.primary_label || ''} onChange={(e) => setSlide(i, 'primary_label', e.target.value)} placeholder="Label (e.g. Book a session)" className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm mb-2" data-testid={`hero-plabel-${i}`} />
                <input value={s.primary_link || ''} onChange={(e) => setSlide(i, 'primary_link', e.target.value)} placeholder="Link (e.g. /facilitator-and-teachers)" className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid={`hero-plink-${i}`} />
              </div>
              <div className="rounded-2xl bg-cream/40 p-3">
                <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1.5">Secondary button (optional)</div>
                <input value={s.secondary_label || ''} onChange={(e) => setSlide(i, 'secondary_label', e.target.value)} placeholder="Label (e.g. Explore classes)" className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm mb-2" data-testid={`hero-slabel-${i}`} />
                <input value={s.secondary_link || ''} onChange={(e) => setSlide(i, 'secondary_link', e.target.value)} placeholder="Link (e.g. /meditate)" className="w-full bg-white rounded-lg px-3 py-2 border border-rose/50 text-sm" data-testid={`hero-slink-${i}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-mahogany/55 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Links can be internal (e.g. <code>/meditate</code>) or full URLs. Manual slides show first, then upcoming events. A slide only appears within its Show-from/until dates.</p>
      <button onClick={save} disabled={saving} className="pill-btn" data-testid="hero-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save home hero</button>
    </div>
  );
};

const BlogSection = () => {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [posts, setPosts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/home/blog').then((r) => { setCfg(r.data.config); setPosts(r.data.posts || []); })
      .catch(() => setCfg({ mode: 'latest', limit: 5, slugs: [] }));
  }, []);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));
  const selected = (cfg?.slugs || []);
  const bySlug = Object.fromEntries(posts.map((p) => [p.slug, p]));
  const available = posts.filter((p) => !selected.includes(p.slug));
  const addSlug = (slug) => set('slugs', [...selected, slug]);
  const rmSlug = (slug) => set('slugs', selected.filter((s) => s !== slug));
  const moveSlug = (i, dir) => { const a = [...selected]; const j = i + dir; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; set('slugs', a); };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/home/blog', { mode: cfg.mode, limit: Number(cfg.limit) || 5, slugs: cfg.mode === 'featured' ? selected : [] });
      setCfg(r.data); toast({ title: 'Home blog saved' });
    } catch (e) { toast({ title: 'Could not save', description: errText(e) }); } finally { setSaving(false); }
  };

  if (!cfg) return <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-home-blog">
      <div className="rounded-3xl bg-white/70 border border-rose/40 p-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs uppercase tracking-widest text-mahogany/70">Show</span>
          {[['latest', 'Latest posts'], ['featured', 'Hand-picked']].map(([v, l]) => (
            <button key={v} onClick={() => set('mode', v)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${cfg.mode === v ? 'bg-mahogany text-cream border-mahogany' : 'bg-white text-mahogany border-rose/50'}`} data-testid={`blog-mode-${v}`}>{l}</button>
          ))}
          <label className="ml-auto text-xs uppercase tracking-widest text-mahogany/70 flex items-center gap-2">How many
            <input type="number" min="1" max="12" value={cfg.limit} onChange={(e) => set('limit', Number(e.target.value))} className="w-16 bg-white rounded-lg px-2 py-1.5 border border-rose/50 text-sm" data-testid="blog-limit" />
          </label>
        </div>

        {cfg.mode === 'featured' && (
          <div className="mt-4 grid md:grid-cols-2 gap-4" data-testid="blog-picker">
            <div>
              <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1.5">Chosen (in order)</div>
              {selected.length === 0 && <p className="text-xs text-mahogany/50">Add posts from the right.</p>}
              <div className="space-y-1.5">
                {selected.map((slug, i) => (
                  <div key={slug} className="flex items-center gap-2 rounded-xl bg-cream/50 border border-rose/30 px-2.5 py-1.5" data-testid={`blog-chosen-${slug}`}>
                    <span className="text-sm text-burgundy flex-1 truncate">{bySlug[slug]?.title || slug}</span>
                    <button onClick={() => moveSlug(i, -1)} disabled={i === 0} className="p-1 text-mahogany/60 disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
                    <button onClick={() => moveSlug(i, 1)} disabled={i === selected.length - 1} className="p-1 text-mahogany/60 disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => rmSlug(slug)} className="p-1 text-red-500/70 hover:text-red-600" data-testid={`blog-remove-${slug}`}><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-mahogany/60 mb-1.5">Available posts</div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                {available.map((p) => (
                  <button key={p.slug} onClick={() => addSlug(p.slug)} className="w-full flex items-center gap-2 rounded-xl bg-white border border-rose/30 px-2.5 py-1.5 text-left hover:bg-rose-soft/30" data-testid={`blog-add-${p.slug}`}>
                    <Plus className="w-3.5 h-3.5 text-mahogany/60" />
                    <span className="text-sm text-burgundy flex-1 truncate">{p.title}</span>
                    {p.category && <span className="text-[10px] uppercase tracking-widest text-mahogany/50">{p.category}</span>}
                  </button>
                ))}
                {available.length === 0 && <p className="text-xs text-mahogany/50">All posts selected.</p>}
              </div>
            </div>
          </div>
        )}
      </div>
      <button onClick={save} disabled={saving} className="pill-btn" data-testid="blog-save">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Save home blog</button>
    </div>
  );
};

const AdminHomeHero = () => (
  <div className="space-y-8">
    <div>
      <div className="eyebrow mb-2">Content</div>
      <h1 className="font-serif-display text-4xl text-burgundy">Home <span className="font-italic-display text-rose-italic">Page</span></h1>
      <p className="text-sm text-mahogany/70 mt-1">Manage the rotating hero slideshow and which blog posts appear on the landing page.</p>
    </div>
    <section>
      <h2 className="font-serif-display text-2xl text-burgundy mb-3">Hero slideshow</h2>
      <HeroSection />
    </section>
    <section className="pt-4 border-t border-rose/30">
      <h2 className="font-serif-display text-2xl text-burgundy mb-3">Home blog list</h2>
      <BlogSection />
    </section>
  </div>
);

export default AdminHomeHero;
